import * as z from 'zod'
import { noteNameSchema } from './note.js'
import { scaleSchema } from './scale.js'

/**
 * The melody suggestion contract, shared by `POST /nlp/melody-suggestions` and its clients.
 *
 * F.2.1 (`docs/superpowers/specs/2026-05-14-melody-suggestions-v1.md`) is a single scale-constrained candidate.
 * Melody toolkit M4 (`docs/superpowers/plans/2026-10-23-melody-toolkit-plan.md`, step 4.0) adds the singer's range,
 * two or three options with a one-line label each, and a failure payload that says what failed and whether the
 * failure was charged. Every new request field is optional, so a client that predates M4 keeps working.
 */

export const melodyDurations = ['1n', '2n', '4n', '8n', '16n'] as const

const melodyDurationSchema = z.enum(melodyDurations).describe('Note duration — whole, half, quarter, eighth, sixteenth')

export type MelodyDuration = z.infer<typeof melodyDurationSchema>

export const melodyNoteSchema = z.object({
  pitch: noteNameSchema.describe('Note name in the scale, e.g., "C", "D#"'),
  octave: z.number().int().min(0).max(8).describe('Octave number; 3-5 typical for singable melodies'),
  duration: melodyDurationSchema,
  position: z.number().describe('Beats from start of section (0-indexed)'),
})

export type MelodyNote = z.infer<typeof melodyNoteSchema>

/** A chord under the melody, with its real timing in beats from the start of the section */
export const melodyChordRefSchema = z.object({
  root: noteNameSchema,
  /**
   * Free-form chord variant string (e.g., '', 'm', '7', 'maj7'). Validated downstream by `ChordLib.getNotes`; an
   * unknown variant simply yields an empty chord-tone set.
   */
  variant: z.string(),
  position: z.number().describe('Beats from the start of the section'),
  duration: z.number().positive().describe('Length in beats'),
})

export type MelodyChordRef = z.infer<typeof melodyChordRefSchema>

const midiNoteSchema = z.number().int().min(0).max(127)

/** The lowest and highest note a melody may use, as MIDI note numbers, inclusive */
export const melodyRangeSchema = z
  .object({
    low: midiNoteSchema.describe('Lowest MIDI note, inclusive'),
    high: midiNoteSchema.describe('Highest MIDI note, inclusive'),
  })
  .refine(range => range.low <= range.high, { message: 'range.low must not be above range.high' })

export type MelodyRange = z.infer<typeof melodyRangeSchema>

/** C3 to B5: what the server used before a request could carry a range */
export const defaultMelodyRange: MelodyRange = { low: 48, high: 83 }

/** The most notes a continuation's phrase may carry: it bounds the prompt, and so the cost */
export const maxPhraseNotes = 64

/** The longest continuation a request may ask for, in beats */
export const maxContinuationBeats = 64

/**
 * The selected phrase that `Keep going` continues (melody toolkit M7). Its notes are context: the server writes new
 * notes after them and never changes them.
 */
export const melodyPhraseSchema = z.object({
  notes: z.array(melodyNoteSchema).min(1).max(maxPhraseNotes).describe('The selected notes, in section beats'),
})

export type MelodyPhrase = z.infer<typeof melodyPhraseSchema>

export const melodySuggestionRequestSchema = z
  .object({
    scale: scaleSchema,
    /** At least one chord, except for a continuation, which may run over no chords (a drone, or standalone) */
    chords: z.array(melodyChordRefSchema),
    bpm: z.number().positive().optional(),
    beatsPerMeasure: z.number().positive().optional(),
    /** Free-text style hint. F.2.1 ignores section type. */
    style: z.string().optional(),
    range: melodyRangeSchema.describe('The singer’s range. Absent: C3 to B5').optional(),
    /**
     * How many options to write. The server writes 2 or 3 when this is set, clamping anything else into that span.
     * Absent (a client that predates M4): one candidate, as before.
     */
    candidateCount: z.number().int().min(1).max(5).optional(),
    phrase: melodyPhraseSchema
      .describe('Present for Keep going: write a continuation of this phrase instead of a whole line')
      .optional(),
    continuationBeats: z
      .number()
      .positive()
      .max(maxContinuationBeats)
      .describe('How long the continuation is, in beats. Absent: as long as the phrase, ending at the last chord')
      .optional(),
  })
  .refine(request => request.chords.length > 0 || request.phrase !== undefined, {
    message: 'chords must not be empty unless a phrase is continued',
    path: ['chords'],
  })

export type MelodySuggestionRequest = z.infer<typeof melodySuggestionRequestSchema>

export const melodyCandidateSchema = z.object({
  notes: z.array(melodyNoteSchema).describe('Ordered melody notes'),
  label: z.string().describe('One line describing what this option does, e.g. "Echoes your rhythm, lands on the 3rd."'),
  /** @deprecated The same text as `label`, kept for clients that predate M4. Remove once they have reloaded. */
  rationale: z.string().optional(),
})

export type MelodyCandidate = z.infer<typeof melodyCandidateSchema>

export const melodySuggestionResponseSchema = z.object({
  candidates: z.array(melodyCandidateSchema).describe('Generated melody candidates'),
  scale: scaleSchema.describe('Scale that was used to generate (echoed back for client confirmation)'),
  tokens: z.number().describe('Total tokens consumed by the LLM call'),
})

export type MelodySuggestionResponse = z.infer<typeof melodySuggestionResponseSchema>

/**
 * The body of a 422 when no option survived. The client picks its failure line from `failure`, and shows "not
 * counted" only when `charged` is present: the server sets it only when it did not charge.
 */
export const melodySuggestionFailureSchema = z.object({
  statusCode: z.literal(422),
  message: z.string(),
  failure: z.object({
    chordIndex: z.number().int().min(0).describe('Index into the request’s chords of the chord that failed').optional(),
    position: z.number().describe('Beats from the start of the section where it failed').optional(),
    isRangeBinding: z.boolean().describe('True when the singer’s range was the constraint that failed'),
  }),
  charged: z.literal(false).describe('Present only when the server did not charge for the request').optional(),
})

export type MelodySuggestionFailure = z.infer<typeof melodySuggestionFailureSchema>
